import { entityStore } from '~/utility/BinSentry-ui-utility';
import { BnModalMixin } from '../../common/bn-modal-mixin';
import { html, LitEntity, renderIf, SirenActionMixin } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-property';
import '../../common/bn-flash';

class FmmIntegrationModal extends BnModalMixin(SirenActionMixin(LitEntity)) {
  constructor() {
    super();
    this.id = 'fmm-integration-modal';
  }

  static get properties() {
    return {
      creatingSecretUrl: { type: Boolean, attribute: false },
      copying: { type: Boolean, attribute: false },
      downloading: { type: Boolean, attribute: false },
    };
  }

  renderContents() {
    return html`
      <style>
        ${sharedStyles}
        bn-modal#fmm-integration-modal {
          --modal-max-width: 75rem;
          --modal-min-width: min(50rem, 50%);
        }
        bn-modal#fmm-integration-modal bn-flash {
          padding: 0 10px;
        }
        bn-modal#fmm-integration-modal .description {
          padding: 0 20px 10px 20px;
        }
        bn-modal#fmm-integration-modal .status {
          font-weight: bold;
        }
        bn-modal#fmm-integration-modal .action-surface {
          position: relative;
          display: flex;
          flex-direction: row;
        }
        bn-modal#fmm-integration-modal .status-surface {
          position: relative;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }
        bn-modal#fmm-integration-modal .hidden {
          visibility: hidden;
        }
        bn-modal#fmm-integration-modal .load-spinner {
          position: absolute;
          width: 100%;
        }
        bn-modal#fmm-integration-modal .secret-url {
          overflow: hidden;
          width: 100%;
          margin-right: 0.6rem;
        }
        bn-modal#fmm-integration-modal .copy-button {
          white-space: nowrap;
        }
      </style>
      <h3 slot="title">${this.title}</h3>
      <div class="description">
        <p>
          The Feed Mill Manager (FMM) integration allows forwarding Order data to a FMM system.
        </p>
        <p>
          Use the secret URL below to begin synchronizing Orders from BinSentry to your FMM system. Do not
          share this URL with anyone.
        </p>
      </div>

      <bn-flash></bn-flash>

      <bn-surface class="status-surface">
        <div>
          <label>Status:</label>
          <span class="status">${this.entity.properties.secretUrl ? 'Enabled' : 'Disabled'}</span>
        </div>
        <button
          class="button button--outlined"
          @click="${this._onDownload}"
          ?disabled="${this.downloading}"
        >
          ${this.downloading ? 'Downloading Sample' : 'Download Sample File'}
        </button>
      </bn-surface>

      ${renderIf(this.entity.properties.secretUrl, () => html`
        <bn-surface class="action-surface">
          <bn-property
            label="Secret Url"
            value="${this.entity.properties.secretUrl}"
            class="secret-url"
            stacked
            overflow-ellipsis
          ></bn-property>

          <button
            class="button button--outlined copy-button"
            @click="${this._onCopy}"
            ?disabled="${this.copying}"
          >
            ${this.copying ? '✓ Copied' : 'Copy Url'}
          </button>
        </bn-surface>
      `)}

      ${renderIf(this.creatingSecretUrl, () => html`<div class="load-spinner"><bn-loading><p>Creating Secret FMM URL...</p></bn-loading></div>`)}
      ${renderIf(this.entity.hasAction('create-fmm-url'), () => html`
        <bn-surface class="action-surface">
          <bn-action
            class="${this.creatingSecretUrl ? 'hidden' : ''}"
            .href="${this.href}"
            actionname="create-fmm-url"
            @submit="${() => this.creatingSecretUrl = true}"
            @response="${this._onSuccess}"
            @error="${this._onError}"
            suppressflash
          >
            <h3 slot="header">
              Create a Secret Url for FMM
            </h3>
          </bn-action>
        </bn-surface>
      `)}
    `;
  }

  async _onDownload() {
    if (this.downloading) {
      return;
    }

    this.downloading = true;
    let csv;
    try {
      const getFmmExportAction = this.entity.getActionByName('get-fmm-csv-export');
      const fields = this.getActionFields(getFmmExportAction);
      fields.set('queuedIsoDateStart', new Date(0).toISOString());
      const { entity } = await this.submitAction(getFmmExportAction, fields);
      csv = entity.properties.fmmOrdersCsv;
    } catch (e) {
      this._getFlash().clear();
      this._getFlash().critical(html`Unable to download sample file. Error: ${e.message}`);
    } finally {
      this.downloading = false;
    }

    if (!csv) {
      this._getFlash().clear();
      this._getFlash().critical(html`No Orders have been queued for synchronization with FMM. Please synchronize at least one Order and then retry downloading the sample file`);
      return;
    }

    this._downloadStringAsFile('ORDIMP.DAT', csv);
  }

  _downloadStringAsFile(filename, string) {
    const ele = document.createElement('a');
    ele.style.display = 'none';
    ele.setAttribute('href', `data:text/plain;charset=US-ASCII,${encodeURIComponent(string)}`);
    ele.setAttribute('download', filename);
    ele.click();
  }

  _onCopy() {
    if (this.copying) {
      return;
    }

    navigator.clipboard.writeText(this.entity.properties.secretUrl);
    this.copying = true;
    setTimeout(() => this.copying = false, 1500);
  }

  async _onSuccess() {
    this.creatingSecretUrl = false;
    this._getFlash().clear();
    await entityStore.fetchWait(this.getHref('self'));
    this._getFlash().success('A secret url has been created. Fmm integration is enabled.');
  }

  async _onError(event) {
    this.creatingSecretUrl = false;
    this._getFlash().clear();
    this._getFlash().critical(html`Could not create a secret url.<div><code>${event.error.message}</code></div>`);
  }

  _getFlash() {
    return this.renderRoot.querySelector('bn-flash');
  }
}

window.customElements.define('fmm-integration-modal', FmmIntegrationModal);
